import { useMemo, useCallback } from 'react';
import { SConfirmDialog } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { useQuoteMutations } from '../../../../context';
import { EditOptionDetailsDialog } from '../edit-option-details-dialog';
import type { OptionRowHandlers } from '../option-row';

interface UseOptionHandlersParams {
  productId: string;
  handleQuantityChange: (id: string, quantity: number) => void;
  handleSellChange: (
    id: string,
    product: QuoteProductDTO,
    sell: string
  ) => void;
  handleProfitMarginChange: (
    id: string,
    product: QuoteProductDTO,
    totalGrossProfit: string
  ) => void;
  handleProfitMarginPercentageChange: (
    id: string,
    product: QuoteProductDTO,
    profitMarginPercentage: string
  ) => void;
}

export const useOptionHandlers = ({
  productId,
  handleQuantityChange,
  handleSellChange,
  handleProfitMarginChange,
  handleProfitMarginPercentageChange
}: UseOptionHandlersParams): OptionRowHandlers => {
  const { openDialog, closeDialog } = useDialog();
  const {
    refreshSinglePrice,
    updateItemNote,
    updateItemLabels,
    deleteSubitem
  } = useQuoteMutations();

  const handleRefreshOptionPrice = useCallback(
    (optionId: string, sku: string) => {
      if (sku) {
        refreshSinglePrice({ productId: optionId, sku });
      }
    },
    [refreshSinglePrice]
  );

  const handleEditOption = useCallback(
    (optionProduct: QuoteProductDTO) => {
      const optionId = optionProduct?.id || optionProduct?._id;
      openDialog(
        <EditOptionDetailsDialog
          itemId={optionId}
          itemName={optionProduct.name}
          note={optionProduct?.note || ''}
          labelType={optionProduct?.labelType}
          customLabel={optionProduct?.customLabel}
          includeInTotalCost={optionProduct?.includeInTotalCost}
          onClose={() => closeDialog(`edit-option-${optionId}`)}
          onSave={(id, data) => {
            if (data.note !== undefined) {
              updateItemNote.mutate({
                itemId: id,
                note: data.note
              });
            }
            updateItemLabels.mutate({
              itemId: id,
              labelType: data.labelType,
              customLabel: data.customLabel || '',
              includeInTotalCost: data.includeInTotalCost ?? true
            });
          }}
        />,
        { id: `edit-option-${optionId}` }
      );
    },
    [openDialog, closeDialog, updateItemNote, updateItemLabels]
  );

  const handleRemoveOption = useCallback(
    (optionId: string) => {
      openDialog(
        <SConfirmDialog
          type="delete"
          title="Remove Option from the Quote"
          description="This will permanently remove this option from the quote."
          additionalMessage="This action CANNOT be undone."
          onClose={() => closeDialog(`remove-option-${optionId}`)}
          onConfirm={() => {
            deleteSubitem({
              itemId: productId,
              subItemId: optionId
            });
            closeDialog(`remove-option-${optionId}`);
          }}
          confirmText="Remove"
          closeText="Cancel"
        />,
        { id: `remove-option-${optionId}` }
      );
    },
    [openDialog, closeDialog, deleteSubitem, productId]
  );

  return useMemo(
    () => ({
      onQuantityChange: handleQuantityChange,
      onSellChange: handleSellChange,
      onProfitMarginChange: handleProfitMarginChange,
      onProfitMarginPercentageChange: handleProfitMarginPercentageChange,
      onEditOption: handleEditOption,
      onRemoveOption: handleRemoveOption,
      onRefreshPrice: handleRefreshOptionPrice
    }),
    [
      handleQuantityChange,
      handleSellChange,
      handleProfitMarginChange,
      handleProfitMarginPercentageChange,
      handleEditOption,
      handleRemoveOption,
      handleRefreshOptionPrice
    ]
  );
};
